import { HelpCircle } from "lucide-react";
import { formatPilotageCurrency, formatPilotageDecimal } from "@/lib/pilotageUi";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export default function AnnualMarginExplainer({
  year,
  isFutureYear = false,
  scenarioName = "Scénario de référence",
  caRetenu = null,
  coutsRetenus = null,
  margeMontant = null,
  actualMargin = null,
  targetMargin = null,
  marginDeltaPts = null,
  isAvailable = true,
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg border border-lenvia-blue/30 bg-lenvia-blue/10 px-2 py-1 text-[11px] font-medium text-lenvia-blue transition-colors hover:border-lenvia-blue/60 hover:bg-lenvia-blue/20 hover:text-sky-300 focus:outline-none focus:ring-2 focus:ring-lenvia-blue/40"
          aria-label="Comprendre le calcul de la marge annuelle"
          data-testid="annual-margin-explain-btn"
        >
          <HelpCircle className="h-3 w-3" />
          <span>Comprendre le calcul</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 max-w-[95vw] border border-white/10 bg-slate-900 p-4 text-xs text-slate-200 shadow-2xl shadow-black/60"
        align="start"
        sideOffset={8}
        data-testid="annual-margin-explanation"
      >
        <div className="flex items-start justify-between border-b border-white/10 pb-3">
          <div>
            <h4 className="text-sm font-semibold text-white">Comprendre le calcul</h4>
            <p className="text-[11px] text-slate-400">Marge annuelle · Rentabilité sur l'exercice</p>
          </div>
          <span className="rounded-full border border-lenvia-blue/30 bg-lenvia-blue/10 px-2 py-0.5 text-[10px] font-medium text-lenvia-blue">
            {scenarioName}
          </span>
        </div>

        {!isAvailable || actualMargin == null || caRetenu == null || caRetenu <= 0 ? (
          <div
            className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-amber-300 text-xs"
            data-testid="explain-unavailable"
          >
            <div className="font-semibold">Calcul indisponible — données manquantes</div>
            <p className="mt-1 text-[11px] text-amber-200/80">
              Le chiffre d'affaires annuel ou les coûts de l'exercice ne sont pas suffisamment renseignés pour établir le taux de marge annuel.
            </p>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            <div className="flex justify-between items-center py-1 border-b border-white/5" data-testid="explain-ca-retenu">
              <span className="text-slate-400">1. Chiffre d'affaires retenu</span>
              <span className="font-medium text-white">{formatPilotageCurrency(caRetenu)}</span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-white/5" data-testid="explain-couts-retenus">
              <span className="text-slate-400">2. Coûts retenus</span>
              <span className="font-medium text-slate-200">
                {coutsRetenus != null ? formatPilotageCurrency(coutsRetenus) : "—"}
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-white/5" data-testid="explain-montant-marge">
              <span className="text-slate-400">3. Montant de la marge</span>
              <span className={`font-semibold ${margeMontant != null && margeMontant >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {margeMontant != null ? formatPilotageCurrency(margeMontant) : "—"}
              </span>
            </div>
            <div className="py-1 border-b border-white/5" data-testid="explain-formule">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">4. Formule appliquée</span>
                <span className="font-mono text-[11px] text-sky-300">Marge (€) / CA (€) × 100</span>
              </div>
              {margeMontant != null && caRetenu != null && (
                <p className="text-[10px] text-slate-400 mt-0.5">
                  ({formatPilotageCurrency(margeMontant)} / {formatPilotageCurrency(caRetenu)}) × 100
                </p>
              )}
            </div>
            <div className="flex justify-between items-center py-1 border-b border-white/5" data-testid="explain-taux-obtenu">
              <span className="text-slate-400">5. Taux obtenu</span>
              <span className={`font-bold ${actualMargin >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {formatPilotageDecimal(actualMargin)} %
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-white/5" data-testid="explain-objectif-annuel">
              <span className="text-slate-400">6. Objectif annuel</span>
              <span className="font-medium text-white">
                {targetMargin != null ? `${formatPilotageDecimal(targetMargin)} %` : "Non défini"}
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-white/5" data-testid="explain-ecart">
              <span className="text-slate-400">7. Écart</span>
              <span className={`font-semibold ${marginDeltaPts != null && marginDeltaPts >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {marginDeltaPts != null
                  ? `${marginDeltaPts >= 0 ? "+" : ""}${formatPilotageDecimal(marginDeltaPts)} points (${marginDeltaPts >= 0 ? "Favorable" : "Défavorable"})`
                  : "—"}
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-white/5" data-testid="explain-periode">
              <span className="text-slate-400">8. Période utilisée</span>
              <span className="font-medium text-slate-200">
                Exercice {year} ({isFutureYear ? "projection annuelle" : "réalisé YTD"})
              </span>
            </div>
            <div className="flex justify-between items-center py-1 border-b border-white/5" data-testid="explain-scenario">
              <span className="text-slate-400">9. Scénario utilisé</span>
              <span className="font-medium text-slate-200">{scenarioName}</span>
            </div>
            <div className="flex justify-between items-center py-1" data-testid="explain-provenance">
              <span className="text-slate-400">10. Provenance des données</span>
              <span className="font-medium text-slate-300 text-right">P&L opérationnel & CRA</span>
            </div>
          </div>
        )}

        <div className="mt-3 border-t border-white/10 pt-2 text-[10px] text-slate-400">
          <p>💡 Règle de gestion : le taux annuel résulte du cumul des montants de marge et de CA. Les taux mensuels ne sont jamais additionnés.</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
